import { useState } from "react";
import { Alert, Button, CircularProgress, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../../services/Api/apiCall";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import { isPhoneCallProcessing, phoneCallOutcomeLabels, phoneCallRecipientLabel } from "../../services/phoneCallService";
import { PhoneCall } from "../../types/PhoneCall";

type CaseCallDocumentationProps = { phoneCallId: number; onClose: () => void };

export default function CaseCallDocumentation({ phoneCallId, onClose }: CaseCallDocumentationProps) {
  const [outcome, setOutcome] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const queryClient = useQueryClient();
  const { data: call, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.item("phone_calls", phoneCallId),
    queryFn: () => apiGet<PhoneCall>(`/phone_calls/${phoneCallId}`),
    refetchInterval: (query) => {
      const current = query.state.data;
      return current && (!current.finishedDate || isPhoneCallProcessing(current)) ? 2000 : false;
    },
  });

  if (isPending) {
    return (
      <Grid sx={{ textAlign: "center", py: 3 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Telefonat wird geladen …</Typography>
      </Grid>
    );
  }
  if (isError || !call) {
    return <Alert severity="error" action={<Button onClick={() => refetch()}>Erneut laden</Button>}>Das Telefonat konnte nicht geladen werden.</Alert>;
  }

  const isShortCall = call.callDuration !== null && call.callDuration < 5;
  const selectedOutcome = isShortCall ? "notReached" : outcome ?? call.outcome ?? "";
  const currentNote = note ?? call.note ?? "";
  const processing = isPhoneCallProcessing(call);
  const noteRequired = selectedOutcome === "reached" && !call.transcriptionCreated
    && (!call.recordingEnabled || call.processingStatus === "failed" || call.processingStatus === "skipped");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await apiPost("/twilio/document_case_call", { phoneCallId, outcome: selectedOutcome, note: currentNote });
      await queryClient.invalidateQueries({ queryKey: queryKeys.all("phone_calls") });
      onClose();
    } catch {
      setError("Der Vermerk konnte nicht gespeichert werden. Bitte den Status aktualisieren und erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  const retry = async (endpoint: string) => {
    setSaving(true);
    setError("");
    try {
      await apiPost(`/twilio/${endpoint}`, { phoneCallId });
      await refetch();
    } catch {
      setError("Die Verarbeitung konnte nicht gestartet werden. Bitte erneut versuchen; eine gelöschte Aufnahme kann nicht wiederhergestellt werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}><Typography>Angerufen: {phoneCallRecipientLabel(call)}</Typography></Grid>
      <Grid size={12}>
        <TextField select required fullWidth label="Ergebnis" value={selectedOutcome} disabled={saving || isShortCall}
          onChange={(event) => setOutcome(event.target.value)} helperText={isShortCall ? "Telefonate unter 5 Sekunden werden als nicht erreicht gewertet." : "Eine Mailbox gilt als nicht erreicht."}>
          {Object.entries(phoneCallOutcomeLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
      </Grid>
      {!call.finishedDate && <Grid size={12}>
        <Alert severity="info" action={<Button disabled={saving} onClick={() => retry("end_case_call")}>Status aktualisieren</Button>}>Der endgültige Anrufstatus wird noch geladen.</Alert>
      </Grid>}
      {processing && <Grid size={12}><Alert severity="info">Die Zusammenfassung wird erstellt. Du kannst den Dialog schließen und die Zusammenfassung später unter Telefonate ansehen und bearbeiten.</Alert></Grid>}
      {call.processingStatus === "failed" && <Grid size={12}>
        <Alert severity="warning" action={call.recordingEnabled && <Button disabled={saving} onClick={() => retry("reprocess_failed_call")}>Erneut erstellen</Button>}>
          Die automatische Zusammenfassung konnte nicht erstellt werden. Du kannst den Vermerk manuell eingeben.
        </Alert>
      </Grid>}
      <Grid size={12}>
        <TextField fullWidth multiline minRows={5} label="Gesprächsvermerk / Zusammenfassung" value={currentNote}
          required={noteRequired} disabled={saving || processing} onChange={(event) => setNote(event.target.value)}
          helperText={noteRequired ? "Bei erreicht sind mindestens 20 Zeichen erforderlich." : "Die Zusammenfassung kann hier ergänzt und bearbeitet werden."} />
      </Grid>
      {error && <Grid size={12}><Alert severity="error">{error}</Alert></Grid>}
      <Grid size={12}>
        <Button variant="contained" onClick={save} disabled={saving || !call.finishedDate || processing || !selectedOutcome || (noteRequired && Array.from(currentNote.trim()).length < 20)}>Speichern</Button>
      </Grid>
    </Grid>
  );
}
